import {
  Check,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  List,
  LogOut,
  Plus,
  Search,
  ServerCog,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { DesktopClient } from "@orni/domain-typescript";
import type { Session, Snapshot } from "../domain";
import type {
  ConfigurableProviderId,
  ProviderInventory,
} from "../sdk/types";
import type { SyncStatus } from "@orni/sdk-typescript";
import type { DesktopAccessPreferences } from "../desktop-access-preferences";
import type { ThemeMode } from "../theme";
import { ProviderIcon } from "../components/ProviderIcon";
import { ApiManagerPage } from "./ApiManagerPage";
import { AgentManagementPage, ProviderSettingsPage } from "./SettingsPage";
import { useI18n } from "../i18n";

type ShellRightMode = "session" | "settings";
const SESSION_LIST_COLLAPSED_KEY = "orni.desktop.session-list-collapsed";

export interface DesktopSessionShellProps {
  snapshot: Snapshot;
  selectedSessionId: string | null;
  selectedSession: Session | null;
  selectedSessionError: string | null;
  sessionContent: (actions: {
    onOpenSwitcher: () => void;
    onChooseSession: (id: string) => void;
    recentSessions: readonly Session[];
    onToggleSessionList: () => void;
    sessionListCollapsed: boolean;
  }) => ReactNode;
  settingsContent: ReactNode;
  initializationContent: ReactNode;
  settingsSection: "settings" | "api";
  onSettingsSectionChange: (section: "settings" | "api") => void;
  syncStatus: SyncStatus | null;
  onOpenSession: (id: string) => void;
  onCloseSession: () => void;
  onOpenCreate: () => void;
  onOpenImport: () => void;
  onDeleteSession: (id: string) => Promise<void> | void;
  initializationRequired: boolean;
  canCompleteInitialization: boolean;
  onCompleteInitialization: () => Promise<void>;
}

export function DesktopSessionShell({
  snapshot,
  selectedSessionId,
  selectedSession,
  selectedSessionError,
  sessionContent,
  settingsContent,
  initializationContent,
  settingsSection,
  onSettingsSectionChange,
  syncStatus,
  onOpenSession,
  onCloseSession,
  onOpenCreate,
  onOpenImport,
  onDeleteSession,
  initializationRequired,
  canCompleteInitialization,
  onCompleteInitialization,
}: DesktopSessionShellProps) {
  const { t } = useI18n();
  const [rightMode, setRightMode] = useState<ShellRightMode>("session");
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Session | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [initializationBusy, setInitializationBusy] = useState(false);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  const [sessionListCollapsed, setSessionListCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SESSION_LIST_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const orderedSessions = useMemo(
    () => [...snapshot.sessions].sort(compareSessions),
    [snapshot.sessions],
  );
  const visibleSessions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return orderedSessions;
    return orderedSessions.filter((session) =>
      `${session.title} ${session.provider} ${session.workspaceLabel} ${session.deviceName ?? ""}`
        .toLowerCase()
        .includes(normalized),
    );
  }, [orderedSessions, query]);

  // The shell makes one initial choice only. Closing a Session deliberately
  // leaves the right pane empty instead of immediately reopening it.
  useEffect(() => {
    // Wait for the initialization state before choosing a Session. Otherwise
    // a fast Session snapshot could open the newest terminal before the
    // settings inventory arrives, hiding the first-run setup surface.
    if (initialSelectionDone || selectedSessionId || initializationRequired || !orderedSessions.length) return;
    setInitialSelectionDone(true);
    onOpenSession(orderedSessions[0].id);
  }, [initialSelectionDone, initializationRequired, onOpenSession, orderedSessions, selectedSessionId]);

  useEffect(() => {
    if (selectedSessionId) setRightMode("session");
  }, [selectedSessionId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SESSION_LIST_COLLAPSED_KEY, String(sessionListCollapsed));
    } catch {
      // Storage can be unavailable in an embedded/private renderer.
    }
  }, [sessionListCollapsed]);

  const chooseSession = (id: string) => {
    setSwitcherOpen(false);
    setRightMode("session");
    onOpenSession(id);
  };
  const confirmDelete = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await onDeleteSession(deleteTarget.id);
      if (selectedSessionId === deleteTarget.id) onCloseSession();
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className={`desktop-session-shell ${sessionListCollapsed ? "session-list-collapsed" : ""}`}>
      <aside className="desktop-session-list" aria-label={t("nav.sessions")}>
        <header className="desktop-session-list-header">
          <div className="desktop-shell-title">
            <button type="button" className="desktop-shell-title-button" aria-label="OrniWork">
              <strong>OrniWork</strong><ChevronDown size={13} />
            </button>
            <small>{t("nav.sessions") === "Sessions" ? "Chats" : "聊天"}</small>
          </div>
          <div className="desktop-session-list-actions">
            <button type="button" onClick={() => setRightMode("settings")} title={t("nav.settings")} aria-label={t("nav.settings")}>
              <Settings2 size={16} />
            </button>
            <button type="button" className={searchOpen ? "active" : ""} onClick={() => { setSearchOpen((value) => !value); if (searchOpen) setQuery(""); }} title={t("sessions.search")} aria-label={t("sessions.search")}>
              <Search size={16} />
            </button>
            {!initializationRequired ? <button type="button" className="desktop-create-button" onClick={onOpenCreate} title={t("sessions.newSession")} aria-label={t("sessions.newSession")}>
              <Plus size={19} />
            </button> : null}
          </div>
        </header>
        {searchOpen ? <label className="desktop-session-search">
          <Search size={14} />
          <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("sessions.searchPlaceholder")} />
          <button type="button" onClick={() => { if (query) setQuery(""); else setSearchOpen(false); }} aria-label={query ? t("common.clear") : t("common.close")}><X size={13} /></button>
        </label> : null}
        <div className="desktop-session-list-scroll">
          {visibleSessions.length ? visibleSessions.map((session) => (
            <SessionListRow
              key={session.id}
              session={session}
              t={t}
              selected={rightMode === "session" && selectedSessionId === session.id}
              onOpen={() => chooseSession(session.id)}
              onDelete={() => setDeleteTarget(session)}
            />
          )) : (
            <div className="desktop-session-empty">
              <List size={20} />
              <strong>{query ? t("sessions.noMatches") : t("sessions.emptyTitle")}</strong>
              <small>{query ? t("sessions.noMatchesDetail") : t("sessions.emptyDetail")}</small>
              {!query && !initializationRequired ? <div className="desktop-session-empty-actions">
                <button type="button" onClick={onOpenCreate}><Plus size={14} />{t("sessions.newSession")}</button>
                <button type="button" onClick={onOpenImport}><FolderOpen size={14} />{t("sessions.importSession")}</button>
              </div> : null}
            </div>
          )}
        </div>
      </aside>

      <main className="desktop-session-detail">
        {initializationRequired ? (
          <div className="desktop-initialization-view">
            {initializationContent}
            <footer className="desktop-initialization-footer">
              <button type="button" disabled={!canCompleteInitialization || initializationBusy} onClick={() => {
                setInitializationBusy(true);
                void onCompleteInitialization().finally(() => setInitializationBusy(false));
              }}>
                <Check size={14} />
                {initializationBusy ? t("common.loading") : t("initialization.finish")}
              </button>
            </footer>
          </div>
        ) : rightMode === "settings" ? (
          <div className="desktop-settings-panel-wrap">
            <header className="desktop-detail-header">
              <button type="button" onClick={() => setRightMode("session")} aria-label={t("session.back")} title={t("session.back")}>
                <ChevronRight size={16} className="desktop-back-icon" />
              </button>
              <div><strong>{settingsSection === "api" ? t("nav.api") : t("nav.settings")}</strong><small>{settingsSection === "api" ? t("api.lead") : t("settings.lead")}</small></div>
              <div className="desktop-detail-header-actions">
                <SyncStatusButton status={syncStatus} />
                <button className={settingsSection === "settings" ? "active" : ""} type="button" onClick={() => onSettingsSectionChange("settings")} aria-label={t("nav.settings")} title={t("nav.settings")}>
                  <Settings2 size={15} />
                  <span>{t("nav.settings")}</span>
                </button>
                <button className={settingsSection === "api" ? "active" : ""} type="button" onClick={() => onSettingsSectionChange("api")} aria-label={t("nav.api")} title={t("nav.api")}>
                  <ServerCog size={15} />
                  <span>{t("nav.api")}</span>
                </button>
              </div>
            </header>
            {settingsContent}
          </div>
        ) : selectedSession ? (
          <div className="desktop-session-content">{sessionContent({
            onOpenSwitcher: () => setSwitcherOpen(true),
            onChooseSession: chooseSession,
            recentSessions: orderedSessions.slice(0, 10),
            onToggleSessionList: () => setSessionListCollapsed((value) => !value),
            sessionListCollapsed,
          })}</div>
        ) : selectedSessionError ? (
          <div className="desktop-shell-empty" role="alert"><strong>{t("app.session.openFailed")}</strong><small>{selectedSessionError}</small></div>
        ) : (
          <div className="desktop-shell-empty">
            <FolderOpen size={23} />
            <strong>{t("sessions.selectTitle")}</strong>
            <small>{t("sessions.selectDetail")}</small>
            {!initializationRequired ? <div className="desktop-shell-empty-actions">
              <button type="button" onClick={onOpenCreate}><Plus size={14} />{t("sessions.newSession")}</button>
              <button type="button" onClick={onOpenImport}><FolderOpen size={14} />{t("sessions.importSession")}</button>
              {orderedSessions.length ? <button type="button" onClick={() => setSwitcherOpen(true)}><List size={14} />{t("sessions.switchSession")}</button> : null}
            </div> : null}
          </div>
        )}
      </main>

      {switcherOpen ? (
        <CenteredModal title={t("sessions.switchSession")} onClose={() => setSwitcherOpen(false)}>
          <div className="desktop-switcher-list">
            {orderedSessions.map((session) => (
              <button key={session.id} type="button" className={session.id === selectedSessionId ? "active" : ""} onClick={() => chooseSession(session.id)}>
                <span className={`desktop-session-status-dot ${statusTone(session.state)}`} />
                <span>
                  <strong>{displayTitle(session)}</strong>
                  <small>{session.provider}{visibleWorkspaceLabel(session.workspaceLabel) ? ` · ${session.workspaceLabel}` : ""}</small>
                </span>
                {session.id === selectedSessionId ? <Check size={14} /> : null}
              </button>
            ))}
          </div>
        </CenteredModal>
      ) : null}
      {deleteTarget ? (
        <CenteredModal title={t("sessions.deleteSession")} onClose={() => { if (!deleteBusy) setDeleteTarget(null); }}>
          <div className="desktop-delete-dialog">
            <Trash2 size={22} />
            <p>{t("sessions.deleteConfirm", { title: displayTitle(deleteTarget) })}</p>
            <footer>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>{t("common.cancel")}</button>
              <button type="button" className="danger" onClick={() => void confirmDelete()} disabled={deleteBusy}>{deleteBusy ? t("common.loading") : t("common.delete")}</button>
            </footer>
          </div>
        </CenteredModal>
      ) : null}
    </div>
  );
}

function SessionListRow({ session, t, selected, onOpen, onDelete }: { session: Session; t: ReturnType<typeof useI18n>["t"]; selected: boolean; onOpen: () => void; onDelete: () => void }) {
  return (
    <article className={`desktop-session-row ${selected ? "selected" : ""}`}>
      <button type="button" className="desktop-session-row-main" onClick={onOpen}>
        <span className={`desktop-session-row-icon provider-${providerIcon(session.provider)}`}><ProviderIcon provider={providerIcon(session.provider)} size={20} /></span>
        <span className="desktop-session-row-copy">
          <strong>{displayTitle(session)}</strong>
          {visibleWorkspaceLabel(session.workspaceLabel) ? <small>{session.workspaceLabel}</small> : null}
          <em>
            <i
              className={`desktop-session-status-dot ${statusTone(session.state)}`}
              title={sessionStateLabel(session.state, t)}
              aria-label={sessionStateLabel(session.state, t)}
            />
            <time dateTime={session.updatedAt} title={session.updated}>{session.updated}</time>
            {visibleDeviceName(session.deviceName) ? <span title={session.deviceName}>{session.deviceName}</span> : null}
          </em>
        </span>
      </button>
      <button type="button" className="desktop-session-row-delete" onClick={onDelete} aria-label={`Delete ${displayTitle(session)}`} title="Delete session"><Trash2 size={13} /></button>
    </article>
  );
}

function CenteredModal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="desktop-centered-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="desktop-centered-modal" role="dialog" aria-modal="true" aria-label={title}>
        <header><strong>{title}</strong><button type="button" onClick={onClose} aria-label="Close"><X size={15} /></button></header>
        {children}
      </section>
    </div>
  );
}

function compareSessions(left: Session, right: Session): number {
  return (right.updatedAt ?? "").localeCompare(left.updatedAt ?? "") || right.id.localeCompare(left.id);
}

function displayTitle(session: Session): string {
  const title = session.title.trim();
  return title && !/^ses_[a-z0-9_-]+$/iu.test(title) ? title : session.provider;
}

function providerIcon(provider: Session["provider"]): "codex" | "claude" | "opencode" {
  return provider === "Claude Code" ? "claude" : provider === "OpenCode" ? "opencode" : "codex";
}

function statusTone(state: Session["state"]): string {
  return state === "running" ? "running" : state === "waiting" ? "waiting" : state === "error" ? "error" : state === "starting" ? "starting" : "idle";
}

function sessionStateLabel(state: Session["state"], t: ReturnType<typeof useI18n>["t"]): string {
  return state === "running" ? t("status.running") : state === "starting" ? t("status.starting") : state === "waiting" ? t("status.waiting") : state === "stopped" ? t("status.stopped") : state === "exited" ? t("status.exited") : state === "error" ? t("status.error") : state === "idle" ? t("status.idle") : state === "done" ? t("status.completed") : t("status.stopping");
}

function visibleDeviceName(name: string | undefined): boolean {
  return Boolean(name?.trim() && !/^local (?:service|device)$/iu.test(name.trim()));
}

function visibleWorkspaceLabel(label: string | undefined): boolean {
  return Boolean(label?.trim() && !/^local service$/iu.test(label.trim()) && !/^ses_[a-z0-9_-]+$/iu.test(label.trim()));
}

export interface DesktopSettingsPanelProps {
  snapshot: Snapshot;
  client: DesktopClient;
  inventory: ProviderInventory | null;
  onInventoryChange: (inventory: ProviderInventory) => void;
  onPickDirectory: () => Promise<string | null>;
  onSessionRootChange: (path: string) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onManageApi: (provider: ConfigurableProviderId) => void;
  onOpenHistory: (provider: ConfigurableProviderId) => void;
  sessionTransportMode: "local" | "remote";
  showSessionTransportSetting: boolean;
  onSessionTransportModeChange: (mode: "local" | "remote") => void;
  accessPreferences: DesktopAccessPreferences;
  onAccessPreferencesChange: (preferences: DesktopAccessPreferences) => Promise<void>;
  operationStorageScope: string;
  syncStatus: SyncStatus | null;
  syncStatusLoading: boolean;
  onRefreshSync: () => void;
  readOnly?: boolean;
  settingsSection: "settings" | "api";
  onSettingsSectionChange: (section: "settings" | "api") => void;
  accountState: "authenticated" | "signed_out" | "unconfigured";
  accountEmail: string | null;
  onSignOut: () => Promise<void>;
}

export function DesktopSettingsPanel(props: DesktopSettingsPanelProps) {
  const { t } = useI18n();
  const [apiCli, setApiCli] = useState<ConfigurableProviderId>("codex");
  return (
    <div className="desktop-settings-panel">
      <div className="desktop-settings-content">
        {props.settingsSection === "settings" ? <div className="desktop-settings-combined">
          <AccountSettingsCard {...props} />
          <ProviderSettingsPage {...props} />
          <AgentManagementPage {...props} surface="agents" onManageApi={(provider) => { setApiCli(provider); props.onSettingsSectionChange("api"); }} onOpenHistory={props.onOpenHistory} />
        </div> : null}
        {props.settingsSection === "api" ? props.readOnly ? <PreviewSettingsNotice /> : <ApiManagerPage client={props.client} initialCli={apiCli} /> : null}
      </div>
    </div>
  );
}

function PreviewSettingsNotice() {
  const { t } = useI18n();
  return <section className="desktop-settings-preview-notice">
    <Settings2 size={20} />
    <strong>{t("initialization.previewTitle")}</strong>
    <small>{t("initialization.previewDetail")}</small>
  </section>;
}

function AccountSettingsCard({ accountState, accountEmail, onSignOut }: Pick<DesktopSettingsPanelProps, "accountState" | "accountEmail" | "onSignOut">) {
  const { t } = useI18n();
  return <section className="desktop-account-settings-card">
    <div className="desktop-account-settings-copy">
      <strong>{t("app.account.title")}</strong>
      <small>{accountState === "authenticated" ? accountEmail ?? t("app.auth.signedInTitle") : t("home.signIn")}</small>
    </div>
    {accountState === "authenticated" ? <button type="button" onClick={() => void onSignOut()}><LogOut size={14} />{t("app.auth.signOut")}</button> : null}
  </section>;
}

function SyncStatusButton({ status }: { status: SyncStatus | null }) {
  const { t } = useI18n();
  const pending = status?.streams.reduce((sum, stream) => sum + stream.pendingCount, 0) ?? 0;
  const failed = status?.streams.reduce((sum, stream) => sum + stream.failedCount, 0) ?? 0;
  const label = status?.state === "current" ? t("sync.current") : status?.state === "pending" ? t("sync.pending") : status?.state === "error" ? t("sync.error") : t("sync.offline");
  return <div className="desktop-sync-status-wrap">
    <button type="button" className={`desktop-sync-status-button sync-${status?.state ?? "offline"}`} aria-label={label} title={label}>
      <span />
    </button>
    <div className="desktop-sync-status-tooltip" role="tooltip"><strong>{label}</strong><small>{pending} pending · {failed} failed</small></div>
  </div>;
}
